import {
  InformationCircleIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import Modal from "@via/components/modal";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import HowItWorksHelp from "@via/components/how-it-works/how-it-works-help";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox";
import SelectComponent from "@via/components/select/select.tsx";
import Table from "@via/components/table/table.tsx";
import AddCollectionFile from "@via/pages/projects/metadata/components/add-collection-file";
import EditCollectionFile from "@via/pages/projects/metadata/components/edit-collection-file";
import EditCollectionPermission from "@via/pages/projects/metadata/components/edit-collection-permission";
import {
  useDeleteVmetaData,
  useGetVmetaData,
  usePopulatedVmetaData,
  useVmetaCollections,
  useDownloadVmetaData,
  useCheckVmetaDataWritePermission,
} from "@via/pages/projects/metadata/metadata.api.tsx";
import { type SelectOption } from "@via/utils/interfaces.tsx";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useLocation, useSearchParams } from "react-router-dom";

import PremiumDatasetFileFilter from "@via/pages/data/premium-dataset/components/premium-dataset-file-filter";
import {
  FilterItem,
  HIDE_COLUMNS,
  renderCell,
} from "@via/pages/data/premium-dataset/utils/helper";
import { formatDateString } from "@via/utils/helpers";
import {
  VmetaDataResponse,
  VmetaDataResponseBody,
  VmetaDataSetFileDataResponseItem,
  VmetaPermissionV2,
  type CheckVmetaDataWritePermissionResponse,
} from "common";
import { EyeIcon, PencilIcon, GlobeAltIcon } from "@heroicons/react/20/solid";
import { PermissionBadge } from "@via/pages/projects/metadata/collections/components/permission-badge";
import { ROUTES } from "@via/utils/constants";
import { ButtonColor } from "@via/utils/constants.tsx";

interface CollectionsProps {
  canvasId: string;
  initialSelectedCollectionName?: string | null;
  onCollectionSelected?: () => void;
}

const pageSize = 10;

export const SHOW_ALL_RECORDS = "Show all records";
export const RECORD_NOT_FOUND_NOTE = "Record not found";
export const PIN_FILTER_HINT =
  "Search and filters are off while one record is shown. Use Show all records to search again.";
const columnHelper = createColumnHelper<Record<string, unknown>>();

const Collections: FC<CollectionsProps> = ({
  canvasId,
  initialSelectedCollectionName,
  onCollectionSelected,
}) => {
  const location = useLocation();
  const isSampleTrackerRoute =
    location.pathname.startsWith("/sample-trackers/");

  // A record link (spec 2026-10-06 section 2): /sample-trackers/:canvasId?collection=<name>&record=<id>.
  // The URL drives the pin, not the mount, so moving between two record links re-pins.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCollection = searchParams.get("collection");
  const pinnedRecordId = searchParams.get("record");

  const [selectedCollection, setSelectedCollection] =
    useState<SelectOption | null>(null);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState(
    initialSelectedCollectionName || urlCollection || ""
  );
  const [cumulativeCollections, setCumulativeCollections] = useState<
    SelectOption[]
  >([]);

  // State for populated data table
  const [tablePage, setTablePage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>([
    {
      id: "updatedAt",
      desc: true,
    },
  ]);
  const [columnFilters, setColumnFilters] = useState<{ or: FilterItem[] }>({
    or: [],
  });

  const [selectedItems, setSelectedItems] = useState<Record<string, unknown>[]>(
    []
  );

  const [isAddFileModalOpen, setIsAddFileModalOpen] = useState(false);
  const [filesToBeDeleted, setFilesToBeDeleted] = useState<
    Record<string, unknown>[]
  >([]);

  const [deletePermissionResult, setDeletePermissionResult] =
    useState<CheckVmetaDataWritePermissionResponse | null>(null);

  const [isNoEditPermissionModalOpen, setIsNoEditPermissionModalOpen] =
    useState(false);
  const [noEditPermissionItemLabels, setNoEditPermissionItemLabels] = useState<
    string[]
  >([]);
  const [nonEditableItemLabels, setNonEditableItemLabels] = useState<string[]>(
    []
  );
  const [editDatasetUsage, setEditDatasetUsage] = useState<
    { datasetLabel: string; fileLabels: string[]; datasetId: string }[]
  >([]);

  const [collectionFilesToBeEdited, setCollectionFilesToBeEdited] = useState<{
    data: VmetaDataResponseBody[];
    meta: {
      field_types: Record<string, unknown>[];
    };
  } | null>(null);

  const [collectionPermissionsToBeEdited, setCollectionPermissionsToBeEdited] =
    useState<Record<string, unknown>[]>([]);

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const { mutateAsync: deleteVmetaData, isPending: isDeleteLoading } =
    useDeleteVmetaData();

  const {
    mutateAsync: checkWritePermission,
    isPending: isCheckWritePermissionLoading,
  } = useCheckVmetaDataWritePermission();

  const {
    mutateAsync: fetchCollectionFile,
    isPending: isFetchCollectionFileLoading,
  } = useGetVmetaData();

  const { mutateAsync: downloadVmetaData, isPending: isDownloadLoading } =
    useDownloadVmetaData();

  const { data: collections, isLoading: isCollectionsLoading } =
    useVmetaCollections(
      page,
      pageSize,
      [],
      [
        {
          id: "projectID",
          value: canvasId,
        },
        {
          id: "name",
          value: search,
        },
      ]
    );

  // Accumulate collections data to prevent loss on pagination
  useEffect(() => {
    if (!collections) return;

    setCumulativeCollections(prevCollections => {
      const newData = collections.data ?? [];

      if (!newData.length) return [];

      // Append new results while preventing duplicates
      const existingIds = new Set(prevCollections.map(c => c.value));
      const newCollections = newData
        .map(({ label, name }) => ({ label, value: name }))
        .filter(c => !existingIds.has(c.value));

      return [...prevCollections, ...newCollections];
    });
  }, [collections?.data, page, search]);

  // Select the collection asked for by the parent, or by a record link in the URL
  const wantedCollectionName = initialSelectedCollectionName || urlCollection;
  useEffect(() => {
    if (wantedCollectionName && cumulativeCollections.length > 0) {
      const collection = cumulativeCollections.find(
        c => c.value === wantedCollectionName
      );
      if (collection && collection.value !== selectedCollection?.value) {
        setSelectedCollection(collection);
        setSearch("");
        setPage(0);
        onCollectionSelected?.();
      }
    }
  }, [
    wantedCollectionName,
    cumulativeCollections,
    selectedCollection?.value,
    onCollectionSelected,
  ]);

  // Applying a pin, or leaving one: first page, no column filters, no selection. Sort and page
  // size are kept, since they cannot hide a single row. Only when the URL's collection is not
  // among the loaded options, and the picker is not already searching for it, search for it;
  // the accumulator is emptied only together with that search change, exactly as the typed
  // search does, so the new query key (cached or fetched) refills it. Once the selection effect
  // above picks it, the search goes back to "". Results already accumulated for the current
  // search are never dropped (on the first render they are still on their way in).
  useEffect(() => {
    if (!urlCollection) return;
    setTablePage(0);
    setColumnFilters({ or: [] });
    setSelectedItems([]);
    if (
      search !== urlCollection &&
      !cumulativeCollections.some(c => c.value === urlCollection)
    ) {
      setSearch(urlCollection);
      setPage(0);
      setCumulativeCollections([]);
    }
  }, [urlCollection, pinnedRecordId]);

  const isPinned =
    !!pinnedRecordId &&
    !!selectedCollection &&
    selectedCollection.value === urlCollection;

  const showAllRecords = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("record");
      return next;
    });
  }, [setSearchParams]);

  // While pinned the query carries only the record id, so no other filter can hide that row.
  const { data: populatedData, isLoading: isPopulatedDataLoading } =
    usePopulatedVmetaData(
      canvasId,
      (selectedCollection?.value as string) || "",
      isPinned
        ? {
            take: tablePageSize,
            skip: 0,
            filter: [{ id: "_id", value: pinnedRecordId }],
            operatorMap: { _id: "eq" },
            sort,
          }
        : {
            take: tablePageSize,
            skip: tablePage * tablePageSize,
            filter:
              columnFilters.or.length > 0
                ? { or: columnFilters.or }
                : undefined,
            sort,
          }
    );

  // Checkbox selection logic
  const allRowsSelected =
    selectedItems.length > 0 &&
    populatedData?.data &&
    selectedItems.length === (populatedData?.data || []).length;

  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;

  const toggleAllRowsSelected = (): void => {
    const currentPageIds = populatedData?.data.map(item => item._id);
    const isAllSelected = populatedData?.data.every(item =>
      selectedItems.some(selected => selected._id === item._id)
    );

    if (isAllSelected) {
      setSelectedItems(
        prev =>
          prev.filter(
            item =>
              !currentPageIds?.includes(
                (item as VmetaDataSetFileDataResponseItem)._id
              )
          ) || []
      );
    } else {
      const newSelections =
        populatedData?.data.filter(
          item => !selectedItems.some(selected => selected._id === item._id)
        ) || [];
      setSelectedItems(prev => [...prev, ...newSelections]);
    }
  };

  const handleCheckboxToggle = (row: Record<string, unknown>): void => {
    const isSelected = selectedItems.some(item => item._id === row._id);
    if (isSelected) {
      setSelectedItems(prev => prev.filter(item => item._id !== row._id));
    } else {
      setSelectedItems(prev => [...prev, row]);
    }
  };

  // Create checkbox column
  const checkboxColumn = useMemo(
    () =>
      columnHelper.accessor("checkboxColumn", {
        header: () => (
          <IndeterminateCheckbox
            checked={allRowsSelected}
            indeterminate={someRowsSelected}
            onChange={toggleAllRowsSelected}
            onClick={e => {
              e.stopPropagation();
            }}
            className="text-indigo-600"
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            onClick={e => {
              e.stopPropagation();
            }}
            checked={selectedItems.some(({ _id }) => _id === row.original._id)}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={() => {
              row.toggleSelected();
              handleCheckboxToggle(row.original);
            }}
            className="text-indigo-600"
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        size: 50,
      }),
    [selectedItems, allRowsSelected, someRowsSelected, populatedData]
  );

  // Create updatedAt column
  const updatedAtColumn = useMemo(
    () =>
      columnHelper.accessor("updatedAt", {
        cell: info => {
          const value = info.getValue() as Date;
          return (
            <span className="whitespace-nowrap px-1">
              {formatDateString(value, "LL")}
            </span>
          );
        },
        header: () => <span className="px-1">Updated At</span>,
        size: 120,
      }),
    []
  );

  // Create perms column
  const permissionColumn = useMemo(
    () =>
      columnHelper.accessor("perms", {
        cell: info => {
          const permissions = (info.getValue() as VmetaPermissionV2) || [];
          const everyonePermissions =
            permissions.filter(perm => perm.type === "everyone") || [];
          const readPermissions =
            permissions.filter(perm => perm.read && perm.type !== "everyone") ||
            [];
          const writePermissions =
            permissions.filter(
              perm => perm.write && perm.type !== "everyone"
            ) || [];
          const rowId = (info.row.original as { _id?: string })?._id || "";

          return (
            <div className="flex flex-row gap-2">
              {everyonePermissions.length > 0 && (
                <PermissionBadge
                  icon={GlobeAltIcon}
                  count={everyonePermissions.length}
                  bgColor="bg-gray-100"
                  textColor="text-gray-500"
                  iconColor="text-gray-500"
                  permissions={everyonePermissions}
                  permissionType="everyone"
                  rowId={rowId}
                />
              )}
              <PermissionBadge
                icon={EyeIcon}
                count={readPermissions.length}
                bgColor="bg-blue-100"
                textColor="text-blue-500"
                iconColor="text-blue-500"
                permissions={readPermissions}
                permissionType="read"
                rowId={rowId}
              />
              <PermissionBadge
                icon={PencilIcon}
                count={writePermissions.length}
                bgColor="bg-green-100"
                textColor="text-green-500"
                iconColor="text-green-500"
                permissions={writePermissions}
                permissionType="write"
                rowId={rowId}
              />
            </div>
          );
        },
        header: () => <span className="px-1">Permissions</span>,
      }),
    []
  );

  // Create dynamic columns based on meta.field_types
  const columns = useMemo(() => {
    if (!populatedData) return [];

    const columns =
      populatedData?.meta?.field_types?.map(field => {
        return columnHelper.accessor(field?.name as string, {
          cell: info => (
            <span className="whitespace-nowrap px-1">
              {renderCell(info, field)}
            </span>
          ),
          header: () => <span className="px-1">{field?.label as string}</span>,
        });
      }) || [];

    return [checkboxColumn, ...columns, permissionColumn, updatedAtColumn];
  }, [populatedData, checkboxColumn, permissionColumn, updatedAtColumn]);

  useEffect(() => {
    if (populatedData && Object.keys(columnVisibility).length === 0) {
      const initialVisibility = {};
      columns.forEach(col => {
        if (col.accessorKey !== "checkboxColumn") {
          initialVisibility[col.id ?? col.accessorKey ?? ""] =
            !HIDE_COLUMNS.includes(col.accessorKey ?? "");
        }
      });
      setColumnVisibility(initialVisibility);
    }
  }, [populatedData, columns]);

  const fetchMoreCollections = useCallback(() => {
    setPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((collections?.results ?? 0) / pageSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [collections?.results]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === search) return;
    setPage(0);
    setSearch(value);
    setCumulativeCollections([]);
  }, 300);

  const handleOpenEditModal = async () => {
    const collectionName = selectedCollection?.value as string;
    if (!collectionName) return;

    // Check write permissions for selected items first
    const ids = Array.from(
      new Set(selectedItems.map(getItemId).filter(Boolean))
    );
    if (ids.length === 0) return;

    let permRes: CheckVmetaDataWritePermissionResponse | null = null;
    try {
      const res = await checkWritePermission({
        canvasId,
        collectionName,
        ids,
      });
      permRes = res.data;
    } catch (e) {
      console.error("Error checking write permission:", e);
      permRes = null;
    }

    if (!permRes || permRes.writeAllowed.length === 0) {
      setNoEditPermissionItemLabels(
        Array.from(
          new Set(
            selectedItems.map(it => getItemDisplayValue(it)).filter(Boolean)
          )
        )
      );
      setIsNoEditPermissionModalOpen(true);
      return;
    }

    const allowedSet = new Set(permRes.writeAllowed);
    const editableItems = selectedItems.filter(it => {
      const id = getItemId(it);
      return id ? allowedSet.has(id) : false;
    });
    const nonEditableItems = selectedItems.filter(it => {
      const id = getItemId(it);
      return id ? !allowedSet.has(id) : true;
    });

    setNonEditableItemLabels(
      Array.from(
        new Set(
          nonEditableItems.map(it => getItemDisplayValue(it)).filter(Boolean)
        )
      )
    );

    // Build dataset usage mapping (only relevant for file collection)
    if (collectionName === "file" && permRes.inUseDatasets?.length) {
      const usage = permRes.inUseDatasets
        .map(ds => {
          const datasetLabel = ds.name || ds.id;
          const fileLabels = Array.from(
            new Set(
              (ds.fileIds || [])
                .map(fid => {
                  const matched = selectedItems.find(
                    it => getItemId(it) === fid
                  );
                  return matched ? getItemDisplayValue(matched) : fid;
                })
                .filter(Boolean)
            )
          );
          return { datasetLabel, fileLabels, datasetId: ds.id };
        })
        .filter(x => x.fileLabels.length > 0);
      setEditDatasetUsage(usage);
    } else {
      setEditDatasetUsage([]);
    }

    // Fetch meta/fields using first editable item
    const firstEditable = editableItems[0];
    const filesData = await fetchCollectionFile({
      id: getItemId(firstEditable),
      canvasId: canvasId,
      collectionName,
    });

    const files = {
      data: editableItems as VmetaDataResponseBody[],
      meta: {
        field_types: filesData?.meta?.field_types || [],
      },
    };

    setCollectionFilesToBeEdited(files);
  };

  const handleOpenEditPermissionsModal = () => {
    setCollectionPermissionsToBeEdited(selectedItems);
  };

  const handleOpenDeleteModal = () => {
    setFilesToBeDeleted(selectedItems);
    setDeletePermissionResult(null);
  };

  const getItemDisplayValue = useCallback((item: Record<string, unknown>) => {
    const name = item?.name;
    if (typeof name === "string" && name.trim().length > 0) return name;
    const id = item?.id;
    if (id !== undefined && id !== null && String(id).trim().length > 0) {
      return String(id);
    }
    const _id = item?._id;
    return _id !== undefined && _id !== null ? String(_id) : "";
  }, []);

  const getItemId = useCallback((item: Record<string, unknown>) => {
    const _id = item?._id;
    if (_id !== undefined && _id !== null) return String(_id);
    const id = item?.id;
    if (id !== undefined && id !== null) return String(id);
    return "";
  }, []);

  // When delete modal opens, check write permission for selected ids
  useEffect(() => {
    const collectionName = selectedCollection?.value as string;
    if (!collectionName) return;
    if (!filesToBeDeleted || filesToBeDeleted.length === 0) return;

    const ids = Array.from(
      new Set(filesToBeDeleted.map(getItemId).filter(Boolean))
    );
    if (ids.length === 0) return;

    (async () => {
      try {
        const res = await checkWritePermission({
          canvasId: canvasId,
          collectionName,
          ids,
        });
        setDeletePermissionResult(res.data);
      } catch (e) {
        // If check fails, fall back to showing regular delete confirm
        setDeletePermissionResult(null);
        console.error("Error checking write permission:", e);
      }
    })();
  }, [
    filesToBeDeleted,
    selectedCollection?.value,
    canvasId,
    checkWritePermission,
    getItemId,
  ]);

  const handleDownloadData = useCallback(() => {
    if (!selectedCollection?.value) return;

    downloadVmetaData({
      canvasId,
      collectionName: selectedCollection.value as string,
      filter:
        columnFilters.or.length > 0 ? { or: columnFilters.or } : undefined,
      sort,
    });
  }, [selectedCollection, canvasId, columnFilters, sort, downloadVmetaData]);

  const handleConfirmDelete = async () => {
    try {
      // If permission check says some items are not allowed, do not delete.
      if (
        deletePermissionResult &&
        deletePermissionResult.writeNotAllowed &&
        deletePermissionResult.writeNotAllowed.length > 0
      ) {
        setFilesToBeDeleted([]);
        return;
      }

      const allowedSet = deletePermissionResult?.writeAllowed
        ? new Set(deletePermissionResult.writeAllowed)
        : null;

      await Promise.all(
        filesToBeDeleted
          .filter(file => {
            if (!allowedSet) return true;
            const id = getItemId(file);
            return id ? allowedSet.has(id) : false;
          })
          .map(file =>
            deleteVmetaData({
              canvasId: canvasId,
              collectionName: selectedCollection?.value as string,
              id: getItemId(file),
            })
          )
      );

      setSelectedItems([]);
    } catch (error) {
      console.error("Error deleting files:", error);
    } finally {
      setFilesToBeDeleted([]);
    }
  };

  // Determine route pattern based on current location
  const routePattern = useMemo(() => {
    if (location.pathname.startsWith("/sample-trackers/")) {
      return ROUTES.SAMPLE_TRACKERS_DETAIL;
    }
    return ROUTES.PROJECT_METADATA;
  }, [location.pathname]);

  const getCollectionsContext = useCallback(() => {
    const collectionData = populatedData?.data || [];
    const fieldTypes = populatedData?.meta?.field_types || [];

    return {
      project: {
        id: canvasId,
      },
      collection: selectedCollection
        ? {
            name: selectedCollection.value as string,
            label: selectedCollection.label,
          }
        : null,
      collections: cumulativeCollections.slice(0, 50).map(collection => ({
        name: collection.value as string,
        label: collection.label,
      })),
      data: {
        total: populatedData?.results || 0,
        currentPage: tablePage,
        pageSize: tablePageSize,
        items: collectionData.slice(0, 50).map(item => {
          const itemData: Record<string, unknown> = {
            id: item._id,
            updatedAt: item.updatedAt,
            permissions: item.perms,
          };
          fieldTypes.forEach(field => {
            const fieldName = field?.name as string;
            if (item[fieldName] !== undefined) {
              itemData[fieldName] = item[fieldName];
            }
          });
          return itemData;
        }),
        fieldTypes: fieldTypes.map(field => ({
          name: field?.name,
          label: field?.label,
          type: field?.type,
        })),
        hasMoreItems: (populatedData?.results || 0) > 50,
      },
      filters: columnFilters.or,
      sortBy: sort.length > 0 ? sort[0] : null,
      selectedItemsCount: selectedItems.length,
    };
  }, [
    canvasId,
    selectedCollection,
    cumulativeCollections,
    populatedData,
    tablePage,
    tablePageSize,
    columnFilters,
    sort,
    selectedItems.length,
  ]);

  useRegisterCortexContext({
    routePattern,
    contextName: "Metadata Collections",
    getContext: getCollectionsContext,
    systemPrompt:
      "You are analyzing metadata collections page. Help answer questions about collections, collection data, field types, filtering, and sorting.",
    enabled: !!selectedCollection && !!populatedData,
  });

  return (
    <>
      <div className="flex flex-col text-sm leading-6 text-slate-600">
        <div className="flex items-start justify-between mt-3 gap-4">
          <div className="flex flex-col lg:flex-row items-start gap-3 flex-1">
            <div
              className={`mt-2 ${
                selectedCollection ? "w-full max-w-md" : "w-full"
              }`}>
              <SelectComponent
                name="collectionId"
                options={cumulativeCollections}
                value={selectedCollection}
                isLoading={isCollectionsLoading}
                placeholder="Select Collection"
                prefixIcon={<RectangleStackIcon />}
                onFetchMoreOptions={fetchMoreCollections}
                onInputChange={inputValue =>
                  debouncedHandleInputChange(inputValue)
                }
                enableClear={false}
                onChange={data => {
                  const option = data as SelectOption;
                  setSelectedItems([]); // Reset selection when collection changes
                  // With a collection in the URL, the URL drives the selection: the picker only
                  // rewrites it and drops any pin, so a pin never applies to a collection it does
                  // not belong to, and the selection effect selects once the URL has changed.
                  if (urlCollection !== null) {
                    setSearchParams(prev => {
                      const next = new URLSearchParams(prev);
                      next.set("collection", String(option.value));
                      next.delete("record");
                      return next;
                    });
                    return;
                  }
                  setSelectedCollection(option);
                }}
              />
            </div>
            {selectedCollection && (
              <div className="w-full max-w-xs">
                <PremiumDatasetFileFilter
                  columns={columns}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  setPage={setTablePage}
                  setPageSize={setTablePageSize}
                  // The URL decides: off from the moment a pin is asked for, so a keystroke
                  // queued before a record link cannot apply while its collection loads.
                  disabled={!!pinnedRecordId}
                  disabledHint={PIN_FILTER_HINT}
                />
              </div>
            )}
          </div>
          {selectedCollection && (
            <div className="flex justify-end gap-2 mt-2">
              {isSampleTrackerRoute ? (
                <HowItWorksHelp
                  title={
                    <div className="flex items-center gap-2 pr-10">
                      <InformationCircleIcon className="h-5 w-5 text-blue-700" />
                      <span className="text-xl font-semibold text-gray-900">
                        Sample Tracker
                      </span>
                    </div>
                  }>
                  <div>
                    <p className="mt-4 text-base leading-6 text-gray-700">
                      Sample Trackers help you organize experiment data and file
                      information in one place.
                    </p>

                    <h3 className="mt-4 text-lg font-semibold text-gray-900">
                      How it works
                    </h3>

                    <div className="mt-3 space-y-4 text-base leading-6 text-gray-900">
                      <div>
                        <div className="font-semibold">Collections</div>
                        <div className="mt-1 text-gray-700">
                          A Sample Tracker is made of collections—each one is a
                          list of items with fields you define (like a simple
                          table).
                        </div>
                      </div>

                      <div>
                        <div className="font-semibold">
                          Files collection (created for you)
                        </div>
                        <div className="mt-1 text-gray-700">
                          Every Sample Tracker includes a built-in{" "}
                          <span className="font-semibold">Files</span>{" "}
                          collection with common fields like Name, File1, File2,
                          and GEO ID.
                        </div>
                      </div>

                      <div>
                        <div className="font-semibold">Add fields</div>
                        <div className="mt-1 text-gray-700">
                          Need to track more details? Use{" "}
                          <span className="font-semibold">Add fields</span> to
                          add new fields to the selected Sample Tracker.
                        </div>
                      </div>

                      <div>
                        <div className="font-semibold">Configure fields</div>
                        <div className="mt-1 text-gray-700">
                          To manage existing fields, open the Sample Tracker and
                          go to{" "}
                          <span className="font-semibold">
                            Configuration → Fields
                          </span>
                          .
                        </div>
                      </div>
                    </div>
                  </div>
                </HowItWorksHelp>
              ) : null}
              <ActionButton
                button="Add"
                primary={true}
                onClick={() => setIsAddFileModalOpen(true)}
              />
              <button
                disabled={selectedItems.length === 0}
                onClick={handleOpenEditModal}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-normal text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Edit
              </button>
              <button
                disabled={selectedItems.length === 0}
                onClick={handleOpenDeleteModal}
                className="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-normal text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
                Delete
              </button>
              <button
                disabled={selectedItems.length === 0}
                onClick={handleOpenEditPermissionsModal}
                className="h-[38px] inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50">
                Permissions
              </button>
              <button
                type="button"
                onClick={handleDownloadData}
                disabled={isDownloadLoading}
                className="h-[38px] inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50">
                Download
              </button>
            </div>
          )}
        </div>

        {isPinned && (
          <div className="mt-4 flex items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-amber-800 ring-1 ring-inset ring-amber-300">
              Showing one record
              <button
                type="button"
                onClick={showAllRecords}
                className="font-semibold underline">
                {SHOW_ALL_RECORDS}
              </button>
            </span>
            {!isPopulatedDataLoading &&
              (populatedData?.data?.length ?? 0) === 0 && (
                <span role="status" className="text-gray-600">
                  {RECORD_NOT_FOUND_NOTE}
                </span>
              )}
          </div>
        )}
        {selectedCollection && (
          <div className="mt-6 overflow-auto h-100vh">
            <Table
              isLoading={isPopulatedDataLoading}
              data={populatedData?.data || []}
              columns={columns}
              sort={sort}
              setSort={setSort}
              page={tablePage}
              setPage={setTablePage}
              pageSize={tablePageSize}
              setPageSize={setTablePageSize}
              total={populatedData?.results || 0}
              emptyMessage="No data available."
              applyColumnSize
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              displayCustomColumnVisibility={true}
              customTableClassName="w-max whitespace-nowrap"
              getRowId={row => String((row as { _id?: unknown })._id ?? "")}
              highlightRowId={isPinned ? pinnedRecordId : null}
            />
          </div>
        )}
      </div>

      {isAddFileModalOpen && (
        <AddCollectionFile
          isOpen={isAddFileModalOpen}
          canvasId={canvasId}
          collectionName={selectedCollection?.value as string}
          fieldTypes={populatedData?.meta?.field_types}
          onClose={() => setIsAddFileModalOpen(false)}
        />
      )}

      {filesToBeDeleted?.length > 0 && (
        <DeleteModal
          item={filesToBeDeleted}
          setItem={setFilesToBeDeleted}
          deleteFunction={handleConfirmDelete}
          isLoading={isDeleteLoading || isCheckWritePermissionLoading}
          title="Delete Data"
          hideConfirmButton={!!deletePermissionResult?.writeNotAllowed?.length}
          confirmText="Delete Data"
          message={() => (
            <>
              {isCheckWritePermissionLoading && !deletePermissionResult ? (
                <>Checking permissions…</>
              ) : deletePermissionResult?.writeNotAllowed?.length ? (
                <>
                  You don&apos;t have permission to delete the following items:{" "}
                  <strong className="font-semibold">
                    {Array.from(new Set(deletePermissionResult.writeNotAllowed))
                      .map(id => {
                        const matched = filesToBeDeleted.find(
                          f => getItemId(f) === id
                        );
                        return matched ? getItemDisplayValue(matched) : id;
                      })
                      .join(", ")}
                  </strong>
                  .
                </>
              ) : (
                <>
                  Are you sure you want to delete{" "}
                  <strong className="font-semibold">
                    {Array.from(
                      new Set(
                        filesToBeDeleted
                          .map(file => getItemDisplayValue(file))
                          .filter(Boolean)
                      )
                    ).join(", ")}
                  </strong>
                  ?
                  {(selectedCollection?.value as string) === "file" &&
                  deletePermissionResult?.inUseDatasets?.length ? (
                    <>
                      <br />
                      <br />
                      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        <div className="font-semibold">Warning</div>
                        <div className="mt-1">
                          These files are referenced by the datasets below. If
                          you delete them, they will be{" "}
                          <strong>removed from those datasets</strong>.
                        </div>
                        <div className="mt-2">
                          <strong className="font-semibold">Datasets: </strong>
                          {Array.from(
                            new Set(
                              deletePermissionResult.inUseDatasets
                                .map(d => d.name || d.id)
                                .filter(Boolean)
                            )
                          ).join(", ")}
                          .
                        </div>
                      </div>
                      <details className="mt-3">
                        <summary className="cursor-pointer select-none text-slate-700">
                          Show which files are used in which dataset
                        </summary>
                        <div className="mt-2 space-y-2">
                          {deletePermissionResult.inUseDatasets.map(ds => {
                            const datasetLabel = ds.name || ds.id;
                            const fileLabels = Array.from(
                              new Set(
                                (ds.fileIds || [])
                                  .map(fid => {
                                    const matched = filesToBeDeleted.find(
                                      f => getItemId(f) === fid
                                    );
                                    return matched
                                      ? getItemDisplayValue(matched)
                                      : fid;
                                  })
                                  .filter(Boolean)
                              )
                            );
                            return (
                              <div key={ds.id} className="text-slate-700">
                                <strong className="font-semibold">
                                  {datasetLabel}
                                </strong>
                                {fileLabels.length ? (
                                  <>: {fileLabels.join(", ")}</>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    </>
                  ) : null}
                </>
              )}
            </>
          )}
        />
      )}

      {collectionFilesToBeEdited && (
        <EditCollectionFile
          collectionFiles={collectionFilesToBeEdited}
          canvasId={canvasId}
          collectionName={selectedCollection?.value as string}
          nonEditableItemLabels={nonEditableItemLabels}
          datasetUsage={editDatasetUsage}
          isLoading={isFetchCollectionFileLoading}
          isOpen={!!collectionFilesToBeEdited}
          onClose={() => {
            setCollectionFilesToBeEdited(null);
            setSelectedItems([]);
            setNonEditableItemLabels([]);
            setEditDatasetUsage([]);
          }}
        />
      )}

      <Modal
        open={isNoEditPermissionModalOpen}
        setOpen={open => setIsNoEditPermissionModalOpen(open)}
        title="Edit Data"
        confirmText="Close"
        confirmButtonColor={ButtonColor.Gray}
        hasCancelButton={false}
        onConfirm={async () => {
          setIsNoEditPermissionModalOpen(false);
        }}>
        <div className="flex flex-col py-2 mb-6 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
          <p>
            You don&apos;t have permission to edit the selected items{" "}
            <strong className="font-semibold">
              {noEditPermissionItemLabels.join(", ")}
            </strong>
            .
          </p>
        </div>
      </Modal>

      {collectionPermissionsToBeEdited?.length > 0 && (
        <EditCollectionPermission
          collectionFiles={
            collectionPermissionsToBeEdited as VmetaDataResponse[]
          }
          canvasId={canvasId}
          collectionName={selectedCollection?.value as string}
          isOpen={!!collectionPermissionsToBeEdited}
          isLoading={isFetchCollectionFileLoading}
          onClose={() => {
            setCollectionPermissionsToBeEdited([]);
            setSelectedItems([]);
          }}
        />
      )}
    </>
  );
};
export default Collections;
